import { useState, useEffect, useCallback } from "react";
import api from "./api";

function useFetch<T>(url: string, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Log the outgoing API call
    console.log(`[API Call] Fetching from: ${url}`);
    
    try {
      const { data: result } = await api.get(url);

      // Log successful result
      console.log(`[API Success] URL: ${url}`, result);

      setData(result);
    } catch (e: any) {
      const errorMessage = e.response?.data?.detail ?? "Failed to load data";

      // Log the error
      console.error(`[API Error] URL: ${url}`, {
        message: errorMessage,
        originalError: e
      });

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [url, ...deps]);

  useEffect(() => { fetch_(); }, [fetch_]);

  return { data, loading, error, refetch: fetch_ };
}

export interface FundSummary {
  total_units: string;
  unit_price_today: number;
  management_fee_pct: number;
  operational_cost_pct: number;
  fund_growth_pct: number;
  total_invested: number;
  investor_count: number;
}

export interface FundMetrics {
  total_portfolio_value: number;
  total_cost: number;
  total_gain_loss: number;
  overall_gain_pct: number;
  investor_count: number;
  total_units: number;
  uninvested_amount: number;
  avg_unit_price: number;
  maturity_date: string;
}

export interface TransactionSummary {
  name: string;
  date: string;
  status: string;
  invested_amount: number;
  unit_value: number;
  total_units: number;
}

export interface MonthlyTrends {
  month: string;
  total_value: number;
  total_cost: number;
}

export interface OwnershipPercentage {
  name: string;
  ownership: string;
}
export interface Branch {
  Branch: string;
  Country: string;
  Officer_Name: string;
  Role: string;
}

export interface InvestorData {
  name: string;
  investment_amount: number;
  portfolio_amount: number;
  ownership_percentage: string;
  maturity_date: string
}

export const useFundSummary = () => useFetch<FundSummary>("/fund/summary");

// Modified to set the default parameter value to true
export function useFundMetrics(userOnly: boolean = true) {
  const params = new URLSearchParams();
  params.append("userOnly", String(userOnly));

  const url = `/fund/metrics?${params.toString()}`;

  return useFetch<FundMetrics>(url, [userOnly]);
}


export function usePortfolioTrend(userOnly: boolean = true) {
  const params = new URLSearchParams();
  params.append("userOnly", String(userOnly));

  const url = `/portfolio/trends?${params.toString()}`;

  return useFetch<MonthlyTrends[]>(url, [userOnly]);
}

export function getTransactionHistory(userOnly: boolean = true) {
  const params = new URLSearchParams();
  params.append("userOnly", String(userOnly));

  const url = `/portfolio/history?${params.toString()}`;

  return useFetch<TransactionSummary[]>(url, [userOnly]);
}

export function getInvesterOwnership() {
  const url = `/portfolio/invester_ownership`;

  return useFetch<OwnershipPercentage[]>(url);
}

export function getBranchDetails() {
  const url = `/branch/branch_details`;
  return useFetch<Branch[]>(url, []);
}

export function getInvestorData() {
  const url = `/portfolio/investor_data`;

  return useFetch<OwnershipPercentage[]>(url);
}